/**
 * 统一 Token 管理器
 *
 * 所有 Token 相关的 localStorage 读写操作统一由本模块管理。
 * useAuth、httpClient、authService 等模块均通过本模块存取 Token，
 * 避免多处直接操作 localStorage 导致数据不一致。
 */

// Token 存储 key（导出于外部模块复用）
export const ACCESS_TOKEN_KEY = 'auth_token'
export const REFRESH_TOKEN_KEY = 'auth_refresh_token'
export const AUTH_USER_KEY = 'auth_user'
export const TOKEN_EXPIRES_AT_KEY = 'auth_token_expires_at'

// Access Token 默认有效期（秒），与后端 jwt.access-token-expiration 保持一致
export const DEFAULT_ACCESS_EXPIRATION = 900
// 提前刷新的时间窗口（毫秒），在过期前 2 分钟主动刷新
export const REFRESH_BUFFER_MS = 2 * 60 * 1000

/** 获取 Access Token */
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

/** 获取 Refresh Token */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

/** 获取存储的用户 JSON 字符串 */
export function getUserJson(): string | null {
  return localStorage.getItem(AUTH_USER_KEY)
}

/** 写入令牌（Access Token + Refresh Token + 过期时间） */
export function setTokens(
  accessToken: string,
  refreshToken: string,
  expiresIn?: number,
): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  const ttlMs = (expiresIn ?? DEFAULT_ACCESS_EXPIRATION) * 1000
  localStorage.setItem(TOKEN_EXPIRES_AT_KEY, String(Date.now() + ttlMs))
}

/** 写入用户信息（JSON 字符串） */
export function setUserJson(userJson: string): void {
  localStorage.setItem(AUTH_USER_KEY, userJson)
}

/**
 * 清除所有认证相关的 localStorage 数据
 * 包括：access_token、refresh_token、user、token_expires_at
 */
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(AUTH_USER_KEY)
  localStorage.removeItem(TOKEN_EXPIRES_AT_KEY)
}

/** 严格判断：当前时间 >= 过期时间 → 已过期（用于页面刷新恢复时） */
export function isTokenExpired(): boolean {
  const expiresAtRaw = localStorage.getItem(TOKEN_EXPIRES_AT_KEY)
  if (!expiresAtRaw) return true
  return Date.now() >= Number(expiresAtRaw)
}

/** 宽松判断：当前时间 >= 过期时间 - 2分钟 → 即将过期（用于主动预刷新） */
export function isTokenExpiringSoon(): boolean {
  const expiresAtRaw = localStorage.getItem(TOKEN_EXPIRES_AT_KEY)
  if (!expiresAtRaw) return true
  const expiresAt = Number(expiresAtRaw)
  return Date.now() >= expiresAt - REFRESH_BUFFER_MS
}

/**
 * 是否应在发请求前主动续期。
 *
 * 与 isTokenExpiringSoon 的区别：无过期记录时返回 false。旧会话/异常写入缺这个键时，
 * isTokenExpiringSoon 恒为 true，若直接用于发请求前的预续期，会变成每个请求都先试一次
 * 续期；这里只认「有记录且已进入窗口」，缺记录仍走 401 被动刷新兜底。
 */
export function shouldRefreshAccessToken(): boolean {
  return (
    localStorage.getItem(TOKEN_EXPIRES_AT_KEY) !== null && isTokenExpiringSoon()
  )
}
